import React, {
	SetStateAction,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Drawer,
	Flex,
	Form,
	Input,
	Space,
	Tag,
} from 'antd';
import isEqual from 'lodash.isequal';
import { useHistory } from 'react-router';
import uuid from 'short-uuid';
import { Moment } from 'moment';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { Values } from '@shared/utils/objectEnums';
import {
	BunkerStemAccountTypes,
	BunkerStemAccountTypesLabels,
	BunkerStemQuantityStatus,
	Currencies,
	FuelTypes,
} from '@shared/utils/constants';
import { calculateLiftedBunkerPrice } from '@shared/utils/calculateLiftedBunkerPrice';
import {
	getUtcOffset,
	nowMoment,
	toMoment,
} from '@shared/utils/date';
import type { VoyageWithFixtureAndCounterparty } from '@api/features/voyages/getVoyageDetails';
import type {
	AdditionalCost,
	Parcel,
	StemRob,
} from '@api/features/voyages/bunker-stems/createBunkerStem';
import type { GetBunkerStemsResponse, ParcelWithAccount } from '@api/features/voyages/bunker-stems/getBunkerStems';
import type PerformanceEntry from '@api/models/performance-entry';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import {
	createBunkerStem,
	createSupplier,
	getSuppliers,
	getVoyageDetails,
	getVoyageItinerary,
	updateBunkerStem,
} from '@client/lib/api';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import showErrorNotification from '@client/utils/showErrorNotification';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import { Links } from '@client/utils/links';
import useAttachments from '@client/utils/hooks/useAttachments';
import Select from '../Select';
import Button from '../Button';
import CountryFlag from '../CountryFlag';
import DatePicker from '../DatePicker';
import CurrencySelect from '../CurrencySelect/CurrencySelect';
import EditableCellTableRedux from '../EditableTableRedux/EditableCellTableRedux';
import CreditNoteCheckbox from '../CreditNoteCheckbox';
import styles from './CreateBunkerStemForm.module.css';
import {
	getAdditionalCostTableColumns,
	getParcelTableColumns,
} from './getTableColumns';

const getStemDateRange = (portCall: ItineraryPortCallDto | undefined) => {
	if (portCall == null) {
		return [];
	}

	const start = portCall.arrivalDate;
	const end = portCall.departureDate;

	return [start, end];
};

const required = [{ required: true, message: 'Field is required' }];

type Props = {
	open: boolean;
	onClose: () => void;
	refreshDetails: () => void;
	editing?: GetBunkerStemsResponse[number] | undefined;
	documentId?: number;
	voyageId?: number;
	selectedPortCall?: ItineraryPortCallDto;
	tcInContract?: VoyageWithFixtureAndCounterparty | null;
	editingStem: GetBunkerStemsResponse[number] | undefined | null;
	setEditingStem: React.Dispatch<SetStateAction<GetBunkerStemsResponse[number] | undefined | null>>;
	fixtureCurrency: Values<typeof Currencies>;
};

const CreateBunkerStemForm = ({
	open,
	onClose,
	voyageId,
	selectedPortCall,
	tcInContract,
	refreshDetails,
	editingStem,
	setEditingStem,
	fixtureCurrency,
}: Props) => {
	const history = useHistory();
	const [startEdit, setStartEdit] = useState(false);
	const [suppliers, refreshSuppliers] = useFetchedState(getSuppliers);
	const [creatingNewSupplier, setCreatingNewSupplier] = useState(false);
	const [createCreditNote, setCreateCreditNote] = useState(false);
	const [selectedCurrencyAndRate, setSelectedCurrencyAndRate] = useState({
		currency: fixtureCurrency,
		exchangeRate: 1,
	});
	const [additionalCosts, setAdditionalCosts] = useState<AdditionalCost[]>([]);
	const [parcels, setParcels] = useState<Parcel[]>([]);
	const [robs, setRobs] = useState<StemRob[]>([]);
	const [itineraryEntry, setItineraryEntry] = useState<
		ItineraryPortCallDto | undefined
	>(selectedPortCall);
	const [dirty, setDirty] = useState(false);
	const [paidBy, setPaidBy] = useState<number>(editingStem?.paidBy ?? -1);
	const [form] = Form.useForm();

	const [stemDateMin, stemDateMax] = getStemDateRange(itineraryEntry);

	const [voyage] = useFetchedState(() => getVoyageDetails(
		Number(voyageId),
	));

	const [didSetDefaults, setDidSetDefaults] = useState(false);

	const { paidByOptions, rebillToOptions } = useMemo(() => {
		if (!voyage) {
			return {
				paidByOptions: [],
				rebillToOptions: [],
			};
		}

		let paidByCounterparties = voyage.paidByCounterparties;
		let rebillToCounterparties = voyage.paidByCounterparties;
		const tcInOwnerId = voyage.paidByCounterparties.find((c) => c.type === BunkerStemAccountTypes.TC_IN_OWNER)?.id;

		if (parcels.some((pa) => pa.rebillTo === tcInOwnerId)) {
			paidByCounterparties = paidByCounterparties.filter((c) => c.id !== tcInOwnerId);
		}

		if (paidBy === tcInOwnerId) {
			rebillToCounterparties = paidByCounterparties.filter((c) => c.id !== tcInOwnerId);
		}

		const paidByOptions = paidByCounterparties.map((p) => {
			const label = p.type === BunkerStemAccountTypes.SELF ? '' : ` - ${BunkerStemAccountTypesLabels[p.type]}`;

			return ({
				label: `${p.name}${label}`,
				value: p.id,
			});
		});
		
		const rebillToOptions = rebillToCounterparties.map((p) => {
			const label = p.type === BunkerStemAccountTypes.SELF ? '' : ` - ${BunkerStemAccountTypesLabels[p.type]}`;

			return ({
				label: `${p.name}${label}`,
				value: p.id,
			});
		});
		
		return {
			paidByOptions,
			rebillToOptions,
		};
	}, [voyage, parcels, paidBy]);
	
	const relevantFuelGrades = useMemo(() => {
		if (voyage) {
			return voyage.vesselFuelGrades;
		}

		return Object.keys(FuelTypes);
	}, [voyage]);

	useEffect(() => {
		if (
			!editingStem &&
			voyage?.vessel.consumptionSets?.[0]?.performanceEntries &&
			!didSetDefaults
		) {
			const entries = voyage?.vessel.consumptionSets[0].performanceEntries as PerformanceEntry[];

			const usedFuelTypes = new Set();
			const newParcels: Parcel[] = [];

			entries.forEach((entry) => {
				if (!usedFuelTypes.has(entry.fuelType)) {
					usedFuelTypes.add(entry.fuelType);

					newParcels.push({
						id: uuid.generate(),
						description: null,
						fuelGrade: entry.fuelType,
						quantityStatus: BunkerStemQuantityStatus.ESTIMATED,
						quantity: 0,
						minQuantity: 0,
						maxQuantity: null,
						basisPPT: 0,
						liftedPPT: 0,
						subjectToCosts: false,
						currency: selectedCurrencyAndRate.currency,
						rebillTo: null,
						suppliedAsPartOf: null,
					});
				}
			});

			if (parcels.length === 0) {
				setParcels(newParcels);
			}

			setDidSetDefaults(true);
		}
	}, [
		editingStem,
		voyage,
		didSetDefaults,
		setParcels,
		parcels.length,
		selectedCurrencyAndRate.currency,
	]);

	const goToItin = () => {
		onCloseForm();
		if (voyageId == null) {
			return;
		}

		history.push(`${Links.Voyage.get(voyageId)}/#/itinerary`);
	};

	const formSupplierId = Form.useWatch('supplierId', form);

	// Check if we need to disable save button 
	const dirtyParcels = useMemo(() => {
		if (!editingStem) {
			return false;
		}

		// Parcels can include all kinds of fields that fuck with the isEqual check, so we keep it simple
		const getSimpleParcel = (p: Parcel | ParcelWithAccount) => ({
			quantity: p.quantity,
			minQuantity: p.minQuantity,
			maxQuantity: p.maxQuantity,
			fuelGrade: p.fuelGrade,
			liftedPPT: p.liftedPPT,
			suppliedAsPartOf: p.suppliedAsPartOf,
			rebillTo: p.rebillTo,
			subjectToCosts: p.subjectToCosts,
			quantityStatus: p.quantityStatus,
		});

		const originalParcels = editingStem.parcels;

		const changedIds: number[] = [];
		originalParcels.forEach((parcel) => {
			const localParcel = parcels.find((p) => p.voyageBunkerId === parcel.voyageBunkerId);

			if (
				parcel.voyageBunkerId != null && 
				parcel.isInvoiced && 
				localParcel && 
				!isEqual(getSimpleParcel(parcel), getSimpleParcel(localParcel))
			) {
				changedIds.push(parcel.voyageBunkerId);
			}
		});

		return changedIds.length > 0;
	}, [parcels, editingStem]);

	const disabledReason = useMemo(() => {
		if (parcels.length === 0) {
			return 'Please add at least one parcel.';
		}

		if (parcels.some((p) => p.rebillTo === paidBy)){
			return 'A parcel cannot have the same value in “Paid by” and “Rebill parcel to” fields';
		}

		if (dirtyParcels) {
			return 'You cannot update invoiced parcels';
		}

		return '';
	}, [formSupplierId, paidBy, parcels, dirtyParcels]);

	const parcelsWithLiftedPPT = useMemo(() => parcels.map((p) => ({
		...p,
		liftedPPT: calculateLiftedBunkerPrice({ parcel: p, parcels, additionalCosts }),
	})), [parcels, additionalCosts]);
	
	const {
		useBlocker,
		makeBlockable,
	} = useNavigationBlock();

	useBlocker(dirty);

	const close = () => {
		onClose();
		onClose();
		setDirty(false);
		setParcels([]);
		setAdditionalCosts([]);
		setRobs([]);
		setEditingStem(undefined);
		setStartEdit(false);
		setDidSetDefaults(false);
		setPaidBy(-1);
		form.resetFields();
	};

	const onCloseForm = makeBlockable(close);

	const {
		uploaded,
		modal,
	} = useAttachments({
		attachments: editingStem?.attachments ?? [],
		open,
		setOpen: onCloseForm,
		spacerWidth: 1100,
		attachmentTypeLabel: 'bunkerStem', 
	});

	useEffect(() => {
		if (editingStem != null) {
			form.setFieldsValue(editingStem);
			setPaidBy(editingStem.paidBy ?? -1);
			setSelectedCurrencyAndRate({
				currency: editingStem.currency,
				exchangeRate: editingStem.exchangeRate,
			});
		}
	}, [form, editingStem]);

	const [itinerary] = useFetchedState(() => getVoyageItinerary(voyageId!), [voyageId]);
	const portCalls = useMemo(
		() => itinerary?.filter((i): i is ItineraryPortCallDto => isItemPortCall(i)) ?? [],
		[itinerary],
	);

	useEffect(() => {
		if (itineraryEntry == null && selectedPortCall == null && portCalls != null) {
			const relevantPortCall = portCalls.find((pc) => pc.id === editingStem?.portCallId);

			if (relevantPortCall == null) {
				return;
			}

			setItineraryEntry(relevantPortCall);
		}
	}, [editingStem, itineraryEntry, portCalls, selectedPortCall]);

	useEffect(() => {
		if (!startEdit && editingStem != null) {
			setParcels(editingStem.parcels.map((p) => ({
				...p,
			})));
			setRobs(editingStem.robs);
			setAdditionalCosts(editingStem.additionalCosts);
			setStartEdit(true);
			setDirty(false);
		}
	}, [startEdit, editingStem]);

	const parcelsTable = useMemo(() => {
		if (!voyage) {
			return null;
		}

		return (
			<EditableCellTableRedux<Parcel>
				dataSource={parcelsWithLiftedPPT}
				size="small"
				pagination={false}
				onChange={setParcels}
				emptyText="Empty"
				rowKey="id"
				columns={getParcelTableColumns({
					fixtureType: voyage.fixture.type,
					currency: selectedCurrencyAndRate.currency,
					setParcels,
					rebillToOptions: rebillToOptions,
					paidBy,
					paidByCounterparties: voyage.paidByCounterparties,
					fuelGrades: relevantFuelGrades as Array<keyof typeof FuelTypes>,
				})}
			/>

		);
	}, [parcelsWithLiftedPPT, paidBy, rebillToOptions]);

	const saveBunkerStem = async () => {
		const {
			supplierName,
			invoiceDate,
			stemDate,
			portCallId,
			remarks,
		} = form.getFieldsValue(true);

		if (voyageId == null) {
			return;
		}

		let { supplierId } = form.getFieldsValue(true);

		try {
			if (creatingNewSupplier && supplierName != null) {
				const supplier = await createSupplier(supplierName);
				supplierId = supplier.id;
			}

			const commonData = {
				voyageId,
				supplierId,
				invoiceDate,
				stemDate,
				robs,
				parcels: parcelsWithLiftedPPT,
				attachments: uploaded,
				currency: selectedCurrencyAndRate.currency,
				exchangeRate: selectedCurrencyAndRate.exchangeRate,
				additionalCosts,
				paidBy,
				remarks,
			};

			if (editingStem) {
				await updateBunkerStem({
					id: editingStem.id,
					...commonData,
					generateCreditNote: createCreditNote,
				});
			} else {
				await createBunkerStem({
					...commonData,
					portCallId,
				});
			}

			refreshDetails();
			refreshSuppliers();
			close();
		} catch (e) {
			console.error(e);
			showErrorNotification('Could not save bunker stem', e as Error);
		}
	};

	const stemDateTime = Form.useWatch('stemDate', form);
	const [stemDateTimeOffset, setStemDateTimeOffset] = useState<number | null>(null);

	useEffect(() => {
		const offset = itineraryEntry?.port ?
			getUtcOffset(itineraryEntry.port, stemDateTime?.toDate()) :
			0;
		setStemDateTimeOffset(offset);

		// keep local wall time when port changes
		if (stemDateTime && itineraryEntry?.port?.timezone) {
			form.setFieldsValue({
				stemDate: stemDateTime.clone().tz(itineraryEntry.port.timezone, true),
			});
		}
	}, [itineraryEntry, stemDateTime, form]);

	if (!voyage) {
		return null;
	}

	return (
		<>
			{modal}
			<Drawer
				destroyOnClose
				open={open}
				placement="left"
				width="70%"
				title={editingStem != null ? 'Update bunker stem' : 'Create new bunker stem'}
				onClose={onCloseForm}
				mask={!open}
			>
				<Form
					layout="vertical"
					form={form}
					preserve
				>
					<Form.Item
						name="supplierId"
						label="Supplier"
						initialValue={editingStem?.supplierId}
					>
						<Select
							placeholder="Select Supplier"
							showSearch
							allowClear
							options={[
								{
									label: (<i>Create new...</i>),
									value: 'new',
								},
								...(suppliers ?? []).map((s) => ({
									label: s?.name,
									value: s?.id,
								}))]}
							onChange={(counterpartyId) => {
								setCreatingNewSupplier(counterpartyId === 'new');
							}}
						/>
					</Form.Item>
					<Form.Item
						fieldId="supplierName"
						name="supplierName"
						label="Supplier Name"
						hidden={!creatingNewSupplier}
						rules={[{ required: creatingNewSupplier, message: 'Field is missing' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="portCallId"
						rules={required}
						label="Select port call"
						initialValue={itineraryEntry?.id}
						extra={(
							<>
								{'Don\'t see the port call you\'re looking for? Check the '}
								<Button className={styles.noPaddingButton} onClick={goToItin} type="link">
									Itinerary
								</Button>
							</>
						)}
					>
						<Select
							showSearch
							disabled
							options={portCalls.map((pc) => ({
								value: pc.id,
								label: (
									<Space>
										<Tag className={styles.tag} color={pc.estimated ? 'cyan' : 'geekblue'}>
											{pc.estimated ? 'Estimated' : 'Actual'}
										</Tag>
										<CountryFlag countryCode={pc.port.countryCode} />
										{pc.port?.name}
									</Space>),
							}))}
							placeholder="Select Port"
							onSelect={(pcId) => {
								const portCall = portCalls.find((p) => p.id === pcId);
								setItineraryEntry(portCall);
							}}
						/>
					</Form.Item>
					<Flex
						gap={20}
						align="center"
					>
						<Form.Item
							name="stemDate"
							label="Stem Date"
							initialValue={(itineraryEntry?.arrivalDate ?? itineraryEntry?.estimatedArrivalDate)?.clone()?.add(1, 'minute')}
							rules={required}
							tooltip="Bunker stems are required to happen between the arrival and departure date of a given port call"
						>
							<DatePicker
								key={`stemDate-${stemDateTime ? stemDateTime.toString() : 'none'}-${stemDateTimeOffset}`}
								className={styles.datepicker}
								minDate={stemDateMin ?? undefined}
								maxDate={stemDateMax ?? undefined}
								time
								disableTimezone={false}
								showTimezone
								defaultUtcOffset={stemDateTimeOffset ?? 0}
								onChange={(v) => {
									const val = v as Moment | null;
									form.setFieldsValue({
										endTime: val ? toMoment(val).utcOffset(stemDateTimeOffset ?? 0) : null,
									});
								}}
								onTimezoneChange={(offMin) => {
									form.setFieldsValue({
										endTime: offMin ?
											toMoment(stemDateTime ?? nowMoment()).utcOffset(offMin) :
											null,
									});
								}}
							/>
						</Form.Item>
						<Form.Item
							name="invoiceDate"
							label="Invoice Date"
							initialValue={editingStem?.invoiceDate}
							className={styles.invoiceDate}
						>
							<DatePicker
								className={styles.datepicker}
								disableTimezone
							/>
						</Form.Item>
					</Flex>
					<Form.Item
						label="Paid by"
						name="paidBy"
						initialValue={-1}
					>
						<Select
							options={paidByOptions}
							// @ts-ignore
							onChange={setPaidBy}
						/>
					</Form.Item>
					<Form.Item
						label="Parcel currency"
						name="currency"
						valuePropName="v"
					>
						<CurrencySelect
							baseCurrency={fixtureCurrency}
							value={{
								currency: selectedCurrencyAndRate.currency,
								exchangeRate: selectedCurrencyAndRate.exchangeRate,
							}}
							onChange={(c, e) => {
								setSelectedCurrencyAndRate({
									currency: c,
									exchangeRate: e,
								});
							}}
						/>
					</Form.Item>
					<Form.Item
						name="remarks"
						label="Remarks"
					>
						<Input />
					</Form.Item>
					{tcInContract != null && (
						<Alert
							showIcon
							message={
								`Any parcels created to account ${tcInContract.fixture.tcTerms.fixtureCounterparty.name} 
								will result in a bunker expense also being created on contract ${tcInContract.identifier} 
								(the associated TC-in contract)`
							}
							className={styles.alert}
						/>
					)}
					<Form.Item label="Additional costs">
						<EditableCellTableRedux<AdditionalCost>
							dataSource={additionalCosts}
							size="small"
							pagination={false}
							onChange={setAdditionalCosts}
							emptyText="Empty"
							rowKey="id"
							columns={getAdditionalCostTableColumns({
								additionalCosts,
								currency: selectedCurrencyAndRate.currency,
								setAdditionalCosts,
							})}
						/>
					</Form.Item>
					<Form.Item label="Parcel(s)">
						<Flex vertical gap={10}>
							{parcelsTable}
						</Flex>
					</Form.Item>
					<Button
						disabled={disabledReason !== ''}
						disabledTooltip={disabledReason}
						onClick={saveBunkerStem}
						type="primary"
						confirmTitle={dirtyParcels ? (
							<CreditNoteCheckbox
								description={`One or more parcels have already been invoiced on ${tcInContract?.identifier} -
								do you want to generate a credit note?`}
								createCreditNote={createCreditNote}
								setCreateCreditNote={setCreateCreditNote}
								showCumulativeAlert={false}
							/>
						) : undefined}
					>
						Save Bunker Stem
					</Button>
				</Form>
			</Drawer>
		</>
	);
};

export default CreateBunkerStemForm;
